<template>
  <div>
    <div class="position-relative mb-1">
      <div class="input-sizer">
        <!-- eslint-disable vue/no-v-html -->
        <span
          :class="['spacer', 'form-control', {'all-emoji': isJustEmoji, 'button-overflow': countButtons > 3}]"
          contenteditable="true"
          aria-hidden="true"
          tabindex="-1"
          inert
          v-html="styleVariables(value + '\n', variableReplacementStrings)"
        />
        <!-- eslint-enable -->
        <textarea
          :id="id"
          ref="textarea"
          :name="name"
          aria-label="Message"
          :value="value"
          cols="30"
          :class="['form-control', {'all-emoji': isJustEmoji, 'button-overflow': countButtons > 3}]"
          :disabled="disabled"
          @input="updateValue($event.target.value)"
          @keyup="updateTrigger"
        />
        <span v-if="!disabled && countButtons > 0" class="button-group border rounded-pill">
          <VariablePicker
            v-if="variables"
            :variables="variables"
            :trigger="trigger"
            :textarea="box"
            @input="updateValue"
            @cancelTrigger="cancelTrigger"
          />
          <CannedResponsePicker
            v-if="cannedResponses"
            :canned-responses="cannedResponses"
            :textarea="box"
            :trigger="trigger"
            @input="updateValue"
            @cancelTrigger="cancelTrigger"
          />
          <UploaderWrapper
            v-if="showUploadButton"
            :upload-url="uploadUrl"
            @update:imageName="$emit('update:imageName', $event)"
            @update:imagePath="$emit('update:imagePath', $event)"
          >
            <InputButton
              class="image-upload-invoker"
              icon="image"
              label="Upload image"
            />
          </UploaderWrapper>
          <EmojiPicker
            v-if="showEmojiButton"
            :textarea="box"
            @input="updateValue"
            @cancelTrigger="cancelTrigger"
          />
          <!-- todo: I like having the delete button inside this component, even if it has to be restyled-->
          <InputButton
            v-if="showDeleteButton"
            :danger="true"
            icon="trash-alt"
            label="Delete"
            @click="deleteMessage"
          />
        </span>
      </div>
    </div>
    <div>
      <!-- Note: Variables are excluded from the length when this.variables is set -->
      <!-- Note: Emoji are two or more regular characters in length -->
      {{ complexLength.length }} length <span v-if="hasUnicode">(contains Unicode)</span> <span v-if="complexLength.variables">+ {{ pluralize(complexLength.variables, 'variable') }}</span>
    </div>
  </div>
</template>
<script>
import CannedResponsePicker from "@/FormWidgets/WidgetInputArea/components/CannedResponsePicker.vue";
import EmojiPicker from '@/FormWidgets/WidgetInputArea/components/EmojiPicker.vue'
import InputButton from "@/FormWidgets/WidgetInputArea/components/InputButton.vue";
import UploaderWrapper from "@/FormWidgets/UploaderWrapper/UploaderWrapper.vue";
import VariablePicker from "@/FormWidgets/WidgetInputArea/components/VariablePicker.vue";
import {isJustEmoji, hasUnicode, getVariableAdjustedLength, pluralize, styleVariables} from '@/utils/text';

export default {
  name: 'InputArea',
  // Vue 3:
  // emits: ['input', 'update:imageName', 'update:imagePath'],
  components: {
    CannedResponsePicker,
    EmojiPicker,
    InputButton,
    UploaderWrapper,
    VariablePicker,
  },
  props: {
    /** The `id` for the <textarea>, for form submission */
    id: {
      type: String,
      required: false,
    },
    /** The `name` for the <textarea>, for form submission */
    name: {
      type: String,
      default: 'message',
    },
    cannedResponses: {
      type: Object,
      required: false,
    },
    /** An object (keys are categories) containing objects (keys are variable replacement strings, values are the most human readable version of the variable).
     * Required to account for variables in the length of the message
     */
    variables: {
      type: Object,
      required: false,
    },
    /** Whether the <textarea> is `disabled` */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** The value of the <textarea>. Use `v-model` in parent for two-way binding */
    value: {
      type: String,
      required: true
    },
    /** Whether to show the emoji picker button */
    showEmojiButton: {
      type: Boolean,
      default: true,
    },
    /** Whether to show the delete button */
    showDeleteButton: {
      type: Boolean,
      default: false,
    },
    /** Whether to show length of <textarea> content */
    showLength: {
      type: Boolean,
      default: true,
    },
    /** The URL to upload to, if uploading is available */
    uploadUrl: {
      type: String,
      required: false,
    },
    // todo: Rename. We can upload/send PDFs and one other type of file
    imageName: {
      type: String,
      default: "",
    },
    imagePath: {
      type: String,
      default: "",
    }
  },
  data() {
    return {
      trigger: null, // must not be under provide/inject to be reactive
      uploaderOpen: false,
    }
  },
  computed: {
    countButtons() {
      let count = 0;
      if(this.variables) {
       count++;
      }
      if(this.cannedResponses) {
       count++;
      }
      if(this.showUploadButton) {
       count++;
      }
      if(this.showEmojiButton) {
       count++;
      }
      if(this.showDeleteButton) {
       count++;
      }
      return count;
    },
    // Note: When an image is successfully uploaded, this technically closes the uploader twice:
    // once from `closeAfterFinish: true` in the constructor and once by the onDestroy of UploadWrapper
    showUploadButton() {
      return this.uploadUrl &&  this.imagePath === '';
    },
    isJustEmoji() {
      return isJustEmoji(this.value);
    },
    hasUnicode() {
      return hasUnicode(this.value);
    },
    variableReplacementStrings() {
      if(!this.variables) {
        return [];
      }
      return Object.values(this.variables).flatMap(obj => Object.values(obj));
    },
    complexLength() {
      return getVariableAdjustedLength(this.value, this.variableReplacementStrings);
    },
  },
  methods: {
    styleVariables,
    pluralize,
    box() {
      return this.$refs.textarea;
    },
    updateValue(text) {
      this.$emit('input', text);
    },
    cancelTrigger(refocus) {
      this.updateTrigger(false);
      if(refocus) {
        this.$refs.textarea.focus();
      }
    },
    updateTrigger(event) {
      this.trigger = event;
    },
    deleteMessage() {
      this.$emit('delete');
    }
  },
}
</script>

<style scoped>

div,  >>> div {
  /** todo: properly scope event-logic.scss and remove */
  margin-top: 0 !important;
}

.input-sizer {
  width: 100%;

  /* Use a textarea for input, but hide a span (.spacer) behind it with the same content to take up the appropriate
     space on the page, thus giving the impression that the textarea is being resized to its own content.
     This is the most portable solution for resizing the textarea, and also enables variable highlighting. */
  .spacer, textarea {
    /* Approximately 3 buttons plus their padding: */
    min-height: calc(1.5 * 3rem + 2 * 0.75rem);
    width: 100%;
    /* Approximately 1 button plus its padding: */
    padding-right: 2rem;
    height: 100%;
    max-width: 100%;
    resize: none;
    white-space: pre-wrap;
  }
  /* Override height conditionally if more buttons are added */
  .button-overflow {
    min-height: calc(1.5 * 4rem + 2 * 0.75rem);
  }
  .spacer {
    /** Set text opacity to 0, just in case it doesn't line up with the textarea text */
    color: rgba(0, 0, 0, 0);
    /** Variables are spans inside the spacer */
    & span {
      background: #dee2e6;
    }
  }
  textarea {
    top: 0;
    /* Absolute to InputArea root div */
    position: absolute;
    /* Ensure disabled input has default background styling */
    &:not(:disabled){
      background: transparent;
    }
  }
}

/* Set by methods.changeTextareaFontSize */
.all-emoji {
  font-size: 1.8rem;
}

.button-group {
  position: absolute;
  right: 0.25rem;
  top: 0.5rem;
  display: flex;
  /* Minimize right padding needed to keep text off buttons: */
  flex-direction: column;
}

</style>